import React, { useState } from 'react';
import cn from 'classnames';
import Image from 'next/image';

import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';

import { Section } from 'react-scale/components';

import { customersQuotes } from './quotes';

import styles from './quote.module.css';

const Quote = ({ data, quotes, image, paddingTop, paddingBottom, container }) => {
  const [opacities, setOpacities] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [slideCount, setSlideCount] = useState(0);

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      slides: quotes.length || data.length,
      loop: true,
      breakpoints: {
        '(min-width: 640px)': {
          defaultAnimation: {
            duration: 1500
          }
        }
      },
      defaultAnimation: {
        duration: 300
      },
      created(slider) {
        if (quotes.length > 1) {
          setLoaded(true);
        }
        setSlideCount(slider.slides.length);
      },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      detailsChanged(slider) {
        const newOpacities = slider.track.details.slides.map(slide => slide.portion);
        setOpacities(newOpacities);
      }
    },
    [
      slider => {
        let timeout;
        let mouseOver = false;
        function clearNextTimeout() {
          clearTimeout(timeout);
        }
        function nextTimeout() {
          clearTimeout(timeout);
          if (mouseOver || slider.slides.length <= 1) return;
          timeout = setTimeout(() => {
            slider.next();
          }, 4000);
        }
        slider.on('created', () => {
          slider.container.addEventListener('mouseover', () => {
            mouseOver = true;
            clearNextTimeout();
          });
          slider.container.addEventListener('mouseout', () => {
            mouseOver = false;
            nextTimeout();
          });
          nextTimeout();
        });
        slider.on('dragStarted', clearNextTimeout);
        slider.on('animationEnded', nextTimeout);
        slider.on('updated', nextTimeout);
      }
    ]
  );

  return (
    <Section container={container} relative paddingTop={paddingTop} paddingBottom={paddingBottom}>
      <div className="z-10 sm:space-y-12">
        <div className="relative overflow-hidden rounded-xl bg-codGray">
          <div className="h-[4px] w-full bg-gradient-x absolute bottom-0 sm:hidden" />
          <div className={cn('absolute inset-0 rounded-2xl hidden sm:block', styles.bg)}>
            <div className={cn('h-full', styles.bgGrid)} />
          </div>
          <div ref={sliderRef} className="keen-slider">
            {data
              ? data.map((item, idx) => (
                  <div
                    className="flex flex-col gap-10 sm:gap-8 keen-slider__slide md:flex-row md:flex-nowrap md:items-center"
                    style={{ opacity: opacities[idx] }}
                    key={item.id}
                  >
                    <BlockQuote
                      quote={{
                        content: item.quoteAuthorQuote,
                        profile: item.quoteAuthorName,
                        position: item.quoteAuthorPosition,
                        logo: {
                          src: item.quoteLogo.url,
                          alt: item.quoteLogo.alt,
                          title: item.quoteLogo.title,
                          width: item.quoteLogo.width,
                          height: item.quoteLogo.height
                        }
                      }}
                      image={image}
                    />
                  </div>
                ))
              : quotes.map((q, idx) => {
                  const quote = typeof q === 'string' ? q : q.quote;

                  return (
                    <div
                      className="flex flex-col gap-8 keen-slider__slide lg:flex-row lg:flex-nowrap lg:items-center"
                      style={{ opacity: opacities[idx] }}
                      key={customersQuotes[quote].logo.alt}
                    >
                      <BlockQuote quote={customersQuotes[quote]} image={image} />
                    </div>
                  );
                })}
          </div>
          {loaded && instanceRef.current && slideCount > 1 && (
            <div className="absolute w-full flex items-center justify-center bottom-[13px] z-10">
              <div className="flex justify-center space-x-2 transform bottom-4 max-w-max">
                {[...Array(instanceRef.current.track.details.slides.length).keys()].map(idx => (
                  <span
                    key={idx}
                    onClick={() => {
                      instanceRef.current.moveToIdx(idx);
                    }}
                    className={cn(
                      'h-6 w-6 cursor-pointer flex items-center',
                      currentSlide === idx ? 'opacity-100' : 'opacity-30'
                    )}
                  >
                    <hr className="h-[2px] w-full border-white sm:border-black border-t-2" />
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
        {loaded && instanceRef.current && slideCount > 1 && (
          <div
            className={cn(
              'w-full sm:flex items-center hidden h-full min-h-[40px]',
              quotes.length > 3 ? 'justify-evenly' : 'justify-center gap-4'
            )}
          >
            {data
              ? data.map((item, idx) => (
                  <div
                    key={item.id}
                    onClick={() => {
                      instanceRef.current.moveToIdx(idx);
                    }}
                    className={cn(
                      'cursor-pointer flex justify-center align-middle items-center relative',
                      currentSlide === idx ? 'opacity-100' : 'opacity-30'
                    )}
                  >
                    <Image
                      className="object-fill max-h-10 max-w-[60%] aspect-[24/10]"
                      loading="eager"
                      quality={100}
                      width={item.quoteLogo.width}
                      height={item.quoteLogo.height}
                      src={item.quoteLogo.url}
                      style={{ filter: 'contrast(0) brightness(100)' }}
                    />
                  </div>
                ))
              : quotes.map((q, idx) => {
                  const quote = typeof q === 'string' ? q : q.quote;

                  return (
                    <div
                      key={customersQuotes[quote].logo.alt}
                      onClick={() => {
                        instanceRef.current.moveToIdx(idx);
                      }}
                      className={cn(
                        'cursor-pointer flex justify-center align-middle items-center relative',
                        currentSlide === idx ? 'opacity-100' : 'opacity-30'
                      )}
                    >
                      <Image
                        className="object-fill max-h-10 max-w-[60%] aspect-[24/10]"
                        loading="eager"
                        quality={100}
                        width={customersQuotes[quote].logo.width}
                        height={customersQuotes[quote].logo.height}
                        src={customersQuotes[quote].logo.src}
                        style={{ filter: 'contrast(0) brightness(100)' }}
                      />
                    </div>
                  );
                })}
          </div>
        )}
      </div>
    </Section>
  );
};

const BlockQuote = ({ quote, image }) => (
  <>
    {image && (
      <div className={cn('absolute h-full w-1/2 right-0 opacity-30 hidden sm:block', styles.bgImg)}>
        <Image
          fill
          quality={100}
          src={quote.img.src}
          className="object-cover mix-blend-luminosity grayscale"
        />
      </div>
    )}
    <div className="box-content relative z-10 flex w-auto mb-8 sm:mb-0 p-6 pt-8 pb-0 sm:order-2 min-w-min mx-auto sm:mx-0 lg:mx-auto sm:p-20 sm:pt-0 lg:pb-0 lg:justify-center sm:opacity-80">
      <div className="max-w-[75%] w-32 md:max-w-none">
        <Image
          loading="eager"
          width={quote.logo.width}
          quality={100}
          height={quote.logo.height}
          src={quote.logo.src}
          className={styles.quoteBrand}
        />
      </div>
    </div>
    <div className="lg:flex-1 max-w-[780px] px-6 pb-14 sm:p-20 sm:pb-0 lg:pb-20 lg:pr-0 text-black text-center sm:text-left">
      <blockquote className="mb-8 text-white sm:text-black text-lg sm:text-base md:text-xl">
        {quote.content}
      </blockquote>
      <h4 className="inline-block mb-2 text-lg sm:font-medium leading-10 sm:leading-7 sm:mb-0 text-gradient sm:text-no-gradient">
        {quote.profile}
      </h4>
      <p className="text-lg text-silver sm:text-black sm:text-base">{quote.position}</p>
    </div>
  </>
);

Quote.defaultProps = {
  quotes: ['toyota', 'nuro'],
  image: false,
  paddingTop: false,
  paddingBottom: false,
  container: false
};

export default Quote;
